import { Link } from 'react-router-dom';
import { useSiteData, type VehicleLocation } from '@/data/siteData';

const logoUrl = "/easycar-logo.webp";

const quickLinks = [
  { label: 'Accueil', href: '#accueil' },
  { label: 'Nos véhicules', href: '#vehicules' },
  { label: 'Nos services', href: '#services' },
  { label: 'Financement', href: '#financement' },
  { label: 'Avis clients', href: '#avis' },
  { label: 'Contact', href: '#contact' },
];


export default function Footer() {
  const { hours } = useSiteData();
  const scrollToSection = (href: string) => {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else if (id === 'accueil') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-primary-950 text-white">
      <div className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <img decoding="async" height={192} width={192} src={logoUrl} alt="Easy Car" className="h-12 w-auto rounded-full bg-white" />
              <span className="font-heading font-bold text-lg">Easy Car</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Votre concessionnaire de véhicules d'occasion depuis 2019.
              <br />
              Véhicules révisés, financement et accompagnement administratif.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/Easycar974"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary-500 transition-colors"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill w-4 h-4 flex items-center justify-center" />
              </a>
              <a
                href="https://wa.me/262692505551"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-green-500 transition-colors"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-white/90">
              Liens rapides
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-white/60 text-sm hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to="/mentions-legales"
                  className="text-white/60 text-sm hover:text-white transition-colors"
                >
                  Mentions légales
                </Link>
              </li>
              <li>
                <Link
                  to="/politique-confidentialite"
                  className="text-white/60 text-sm hover:text-white transition-colors"
                >
                  Politique de confidentialité
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-white/90">
              Contact
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-primary-400 mt-0.5" />
                <span className="text-white/70 text-sm">
                  57 Rue du Général de Gaulle<br />
                  97430 Le Tampon, La Réunion
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-primary-400 mt-0.5" />
                <span className="text-white/70 text-sm">
                  153 Route de la Ligne Paradis<br />
                  97410 Saint-Pierre, La Réunion
                </span>
              </li>
              <li>
                <a href="tel:+262692505551" className="flex items-center gap-3 text-white/70 text-sm hover:text-white transition-colors">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-primary-400" />
                  0692 50 55 51
                </a>
              </li>
              <li>
                <a href="mailto:easycar974@gmail.com" className="flex items-center gap-3 text-white/70 text-sm hover:text-white transition-colors">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-primary-400" />
                  easycar974@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-white/90">
              Horaires
            </h4>
            <div className="space-y-4">
              {(['Le Tampon', 'Saint-Pierre'] as VehicleLocation[]).map((location) => (
                <div key={location}>
                  <div className="text-white/80 text-xs font-semibold mb-1">{location}</div>
                  <ul className="space-y-1">
                    {hours[location].map((h) => (
                      <li key={h.day} className="flex justify-between gap-3 text-xs">
                        <span className="text-white/60">{h.day}</span>
                        <span className={h.time === 'Fermé' ? 'text-white/40' : 'text-white/80'}>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="w-full px-4 md:px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/40 text-xs text-center sm:text-left">
            © 2026 CM MOTORS (Easy Car). Tous droits réservés.
          </p>
          <div className="flex gap-4">
            <Link to="/mentions-legales" className="text-white/40 text-xs hover:text-white transition-colors">
              Mentions légales
            </Link>
            <Link to="/politique-confidentialite" className="text-white/40 text-xs hover:text-white transition-colors">
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}